import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Keep Link if needed for breadcrumbs, etc.
import { client, urlFor } from '../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Helper function to format date (reuse from other pages or define locally)
const formatDate = (dateString) => {
  if (!dateString) return '';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Define portableTextComponents for consistent rendering (especially lists)
const portableTextComponents = {
  list: {
    bullet: ({children}) => <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">{children}</ul>,
    number: ({children}) => <ol className="list-decimal pl-8 mb-4 text-gray-700 space-y-2">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li>{children}</li>,
    number: ({children}) => <li>{children}</li>,
  },
  block: {
    normal: ({children}) => <p className="mb-4 text-gray-700 leading-relaxed">{children}</p>,
    // Add other block styles if needed
  },
  marks: {
    strong: ({children}) => <strong className="font-medium">{children}</strong>,
    // Add other marks if needed
  }
};

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600">{message || 'Error loading page content.'}</p>
  </div>
);

const Safety = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = `*[_type == "safetyPage" && _id == "safetyPageContent"][0]{
      ...,
      bannerImage { asset->{ _id, url } }
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
        } else {
          setError('Safety Page content not found.');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching Safety page data:', err);
        setError('Failed to fetch page content.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !pageData) {
    return <ErrorMessage message={error || 'Page data is unavailable.'} />;
  }

  // Destructure for easier access
  const {
    bannerImage,
    bannerTitle = 'Student Safety',
    contentTitle = 'Safety Letter',
    publicationDate,
    bodyContent = []
  } = pageData;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {bannerImage?.asset?.url ? (
            <img
              src={urlFor(bannerImage).width(1200).url()} // Use urlFor
              alt={bannerTitle || 'Safety Banner'}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full object-cover object-center bg-gray-300"></div> // Placeholder
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {bannerTitle}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-[#2a4865]">{contentTitle}</h2>
                {publicationDate && (
                    <p className="text-gray-600">{formatDate(publicationDate)}</p>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6">
                {bodyContent && bodyContent.length > 0 ? (
                    <PortableText 
                        value={bodyContent} 
                        components={portableTextComponents} 
                    />
                ) : (
                    <p>Safety information content is missing.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Safety; 